import { CustomNode, NodeCategory, NodeType } from '@/types/node-types';
import { ConnectionType, CustomEdge } from '@/types/edge-types';

/**
 * Node and edge types that are canvas-only and must be excluded from all
 * graph topology analysis (connectivity, cycle detection, start/end lookup).
 */
/** Canvas-only nodes — not configurable and never emit workflow events. */
export function isCanvasDecorationNode(node: CustomNode): boolean {
  return (
    node.type === NodeType.StickyNoteNode ||
    node.type === NodeType.ArtifactNode ||
    node.type === NodeType.GroupNode
  );
}

/** Nodes that participate in workflow execution (excludes canvas-only placeholders). */
export function isExecutableWorkflowNode(node: CustomNode): boolean {
  if (!node.data?.struct) return false;
  if (node.type === NodeType.SelectNode) return false;
  if (isCanvasDecorationNode(node)) return false;
  return true;
}

export function hasExecutableWorkflowNodes(nodes: CustomNode[]): boolean {
  return nodes.some(isExecutableWorkflowNode);
}

const isTopologyNode = (node: CustomNode): boolean =>
  node.type !== NodeType.SelectNode &&
  !isCanvasDecorationNode(node) &&
  node.data?.struct?.node_type !== NodeCategory.TOOL &&
  node.data?.struct?.node_type !== NodeCategory.ANNOTATION;

/**
 * Returns the subset of nodes and edges that represent the actual workflow
 * topology (excludes SelectNode, canvas decorations, annotation nodes, and
 * tool nodes).
 *
 * Canvas decorations (sticky notes, groups, artifacts) are excluded via
 * `isCanvasDecorationNode`. The `node_type === 'annotation'` check is
 * belt-and-suspenders for nodes that carry the struct signal without a
 * matching React Flow type.
 */
export function getWorkflowGraph(
  nodes: CustomNode[],
  edges: CustomEdge[],
): { nodes: CustomNode[]; edges: CustomEdge[] } {
  const workflowNodes = nodes.filter(isTopologyNode);
  const workflowNodeIds = new Set(workflowNodes.map((n) => n.id));
  const workflowEdges = edges.filter(
    (e) => workflowNodeIds.has(e.source) && workflowNodeIds.has(e.target),
  );
  return { nodes: workflowNodes, edges: workflowEdges };
}

/**
 * Returns connected components as arrays of node IDs.
 * Each isolated node is its own component (counts as 1 workflow).
 * Uses Union-Find for efficiency.
 */
export function getConnectedComponents(
  nodes: CustomNode[],
  edges: CustomEdge[],
): string[][] {
  const parent = new Map<string, string>();

  const find = (id: string): string => {
    if (parent.get(id) !== id) {
      parent.set(id, find(parent.get(id)!));
    }
    return parent.get(id)!;
  };

  const union = (a: string, b: string) => {
    parent.set(find(a), find(b));
  };

  for (const node of nodes) {
    parent.set(node.id, node.id);
  }

  for (const edge of edges) {
    if (parent.has(edge.source) && parent.has(edge.target)) {
      union(edge.source, edge.target);
    }
  }

  const groups = new Map<string, string[]>();
  for (const node of nodes) {
    const root = find(node.id);
    if (!groups.has(root)) groups.set(root, []);
    groups.get(root)!.push(node.id);
  }

  return Array.from(groups.values());
}

/**
 * Detects cycles in a directed graph using DFS.
 * Returns whether a cycle exists and which node IDs are involved.
 */
export function detectCycle(
  nodes: CustomNode[],
  edges: CustomEdge[],
): { hasCycle: boolean; cycleNodeIds: string[] } {
  const adjacency = new Map<string, string[]>();
  for (const node of nodes) {
    adjacency.set(node.id, []);
  }
  for (const edge of edges) {
    // A RepeatUntil back-edge is a bounded semantic edge. The backend
    // compiler removes it before DAG scheduling and executes the enclosed
    // acyclic region through the control carrier.
    if (edge.edge_type === ConnectionType.REPEAT) continue;
    if (adjacency.has(edge.source)) {
      adjacency.get(edge.source)!.push(edge.target);
    }
  }

  // 0 = unvisited, 1 = in current DFS stack, 2 = fully processed
  const state = new Map<string, 0 | 1 | 2>();
  for (const node of nodes) {
    state.set(node.id, 0);
  }

  const cycleNodeIds = new Set<string>();
  const stack: string[] = [];

  const dfs = (nodeId: string): boolean => {
    state.set(nodeId, 1);
    stack.push(nodeId);
    let foundCycle = false;

    for (const neighbor of adjacency.get(nodeId) ?? []) {
      if (state.get(neighbor) === 1) {
        // Back edge — extract only the nodes that form the actual cycle segment
        const startIndex = stack.indexOf(neighbor);
        if (startIndex !== -1) {
          for (let i = startIndex; i < stack.length; i++) {
            cycleNodeIds.add(stack[i]);
          }
        }
        foundCycle = true;
      } else if (state.get(neighbor) === 0) {
        if (dfs(neighbor)) foundCycle = true;
      }
    }

    state.set(nodeId, 2);
    stack.pop();
    return foundCycle;
  };

  let hasCycle = false;
  for (const node of nodes) {
    if (state.get(node.id) === 0) {
      if (dfs(node.id)) hasCycle = true;
    }
  }

  return { hasCycle, cycleNodeIds: Array.from(cycleNodeIds) };
}
